import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ProductKey, PRODUCTS } from "@/lib/products";
import { triggerHaptic } from "@/lib/haptics";
import { toast } from "@/hooks/use-toast";
import { MAX_PER_ORDER, useProductAvailability } from "@/hooks/useProductAvailability";

export interface CartItem {
  product: ProductKey;
  quantity: number;
  name: string;
  price: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: ProductKey, name: string, price: number, quantity?: number) => void;
  removeFromCart: (product: ProductKey) => void;
  updateQuantity: (product: ProductKey, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  /** Highest quantity allowed right now (10 per order, or less if stock is lower). */
  maxQuantity: number;
  soldOut: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("cart");
      if (!saved) return [];
      const parsed = JSON.parse(saved) as CartItem[];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((item) => item.product === "brainCharge")
        .map((item) => ({
          ...item,
          name: PRODUCTS.brainCharge.name,
          price: PRODUCTS.brainCharge.price,
        }));
    } catch {
      return [];
    }
  });

  const availability = useProductAvailability();
  const maxQuantity = availability ? availability.maxQuantity : MAX_PER_ORDER;
  const soldOut = availability ? !availability.inStock : false;
  const clampQty = (q: number) => Math.max(0, Math.min(maxQuantity, Math.floor(q)));

  useEffect(() => {
    try { localStorage.setItem("cart", JSON.stringify(items)); } catch { /* storage unavailable */ }
  }, [items]);

  // Keep the saved cart within the current limit (e.g. after stock drops).
  useEffect(() => {
    setItems((current) => {
      const next = current
        .map((item) => ({ ...item, quantity: Math.min(item.quantity, maxQuantity) }))
        .filter((item) => item.quantity > 0);
      const changed = next.length !== current.length || next.some((n, i) => n.quantity !== current[i].quantity);
      return changed ? next : current;
    });
  }, [maxQuantity]);

  const addToCart = (product: ProductKey, name: string, price: number, quantity: number = 1) => {
    // Trigger haptic feedback on add
    triggerHaptic('success');
    if (typeof window !== 'undefined') window.setTimeout(() => window.dispatchEvent(new Event('proton:open-cart')), 0);
    
    const inCart = items.find((item) => item.product === product)?.quantity ?? 0;
    if (soldOut || inCart + quantity > maxQuantity) {
      toast({
        title: soldOut ? "Currently sold out" : `Limit ${maxQuantity} per order`,
        description: soldOut
          ? "Brain Charge™ is sold out right now."
          : `You can order up to ${maxQuantity} ${maxQuantity === 1 ? "bottle" : "bottles"} at a time.`,
      });
      if (soldOut) return;
    }
    setItems((current) => {
      const existing = current.find((item) => item.product === product);
      if (existing) {
        return current.map((item) =>
          item.product === product
            ? { ...item, quantity: clampQty(item.quantity + quantity) }
            : item
        );
      }
      const q = clampQty(quantity);
      if (q <= 0) return current;
      return [...current, { product, name, price, quantity: q }];
    });
  };

  const removeFromCart = (product: ProductKey) => {
    // Trigger haptic feedback on remove
    triggerHaptic('warning');
    setItems((current) => current.filter((item) => item.product !== product));
  };

  const updateQuantity = (product: ProductKey, quantity: number) => {
    // Trigger light haptic on quantity change
    triggerHaptic('light');
    
    if (quantity <= 0) {
      removeFromCart(product);
      return;
    }
    setItems((current) =>
      current.map((item) =>
        item.product === product ? { ...item, quantity: clampQty(quantity) } : item
      )
    );
  };

  const clearCart = () => {
    triggerHaptic('medium');
    setItems([]);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        maxQuantity,
        soldOut,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
